import React, {useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import {Box, Typography} from '@mui/material';
import {Error, Warning} from '@mui/icons-material';
import {GridColDef} from '@mui/x-data-grid';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrBanner from '../../../../common/arr-banner/ArrBanner';
import ArrDataGrid from '../../../../common/arr-data-grid/ArrDataGrid';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {useGetAIReviewRelevancyDocSummary} from '../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../shared/constants';
import './IssueCodingDocumentDetails.scss';

const NO_DATA_PLACEHOLDER = '--';

interface IssueCodingDocumentDetailsProps {
  showDrawer: boolean;
  onClose: () => void;
  /** Job results route job id → AVR-22456 reviewjobid. */
  jobId: number | string;
  reviewJobSubmittedId: number | string;
  docId: number | string;
}

const getDisplayValue = (value: unknown) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return NO_DATA_PLACEHOLDER;
  }

  return String(value);
};

const formatConfidence = (value: number | string | null | undefined) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return NO_DATA_PLACEHOLDER;
  }
  if (typeof value === 'number') {
    return `${value}%`;
  }
  return String(value);
};

const getIssueCodingResultColumns = (): GridColDef[] => [
  {
    field: 'issueCode',
    headerName: t('ai.review.jobResult.issueCodingDocDetails.issueCode'),
    flex: 1,
    minWidth: 140,
  },
  {
    field: 'responsive',
    headerName: t('ai.review.jobResult.issueCodingDocDetails.responsive'),
    flex: 0.6,
    minWidth: 110,
  },
  {
    field: 'confidence',
    headerName: t('ai.review.jobResult.issueCodingDocDetails.confidence'),
    flex: 0.6,
    minWidth: 110,
  },
  {
    field: 'reason',
    headerName: t('ai.review.jobResult.issueCodingDocDetails.reason'),
    flex: 2,
    minWidth: 200,
  },
];

const IssueCodingDocumentDetails: React.FC<IssueCodingDocumentDetailsProps> = ({
  showDrawer,
  onClose,
  jobId,
  reviewJobSubmittedId,
  docId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);

  const detailRequest = {
    reviewjobid: jobId,
    reviewJobSubmittedId,
  };

  const {
    data: docSummary,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAIReviewRelevancyDocSummary(detailRequest, showDrawer);

  useEffect(() => {
    if (!showDrawer || !isError) {
      setIsErrorToastOpen(false);
      return;
    }
    setIsErrorToastOpen(true);
  }, [isError, showDrawer, reviewJobSubmittedId, jobId]);

  const handleRetry = async () => {
    setIsErrorToastOpen(false);
    try {
      const result = await refetch();
      if (result.isError) {
        setIsErrorToastOpen(true);
      }
    } catch {
      setIsErrorToastOpen(true);
    }
  };

  const isDetailLoading = isLoading || isFetching;
  const shouldShowError = !isDetailLoading && isError;

  const issueCodingRows = useMemo(() => {
    const issueCodingResult = Array.isArray(docSummary?.issueCodingResult)
      ? docSummary.issueCodingResult
      : [];

    return issueCodingResult.map((row, index) => ({
      id: index + 1,
      issueCode: getDisplayValue(row?.issueCode),
      responsive: getDisplayValue(row?.responsive),
      confidence: formatConfidence(row?.confidence),
      reason: getDisplayValue(row?.reason),
    }));
  }, [docSummary?.issueCodingResult]);

  if (!showDrawer) {
    return null;
  }

  const isFlaggedForHumanReview = docSummary?.isFlaggedForHumanReview === true;
  const isExcluded = docSummary?.isExcluded === true;
  const tags = Array.isArray(docSummary?.tags) ? docSummary.tags : [];
  const humanReviewTags = Array.isArray(docSummary?.humanReviewTags)
    ? docSummary.humanReviewTags
    : [];

  return (
    <>
      <ArrDrawer
        className="issuing-code-docs-details"
        ariaLabel="document summary"
        showButton={false}
        isOpen={showDrawer}
        side="right"
        size="large"
        showHeader={false}
        onClose={onClose}
        data-testid="issue-coding-doc-details-drawer"
      >
        <Box
          className="issue-coding-document-details"
          data-testid="issue-coding-doc-details-container"
        >
          <Box
            className="issue-coding-header"
            data-testid="issue-coding-doc-details-header"
          >
            <Typography
              className="typo-text headline3_size doc-id"
              data-testid="issue-coding-doc-details-document-id"
            >
              {String(docId)}
            </Typography>

            <ArrButton
              aria-label={t('common.close')}
              variant="primary"
              size="medium"
              className="close-button"
              dataTestId="issue-coding-doc-details-close-button"
              onClick={onClose}
            >
              {t('common.close')}
            </ArrButton>
          </Box>

          {isDetailLoading ? (
            <Box
              className="issue-coding-doc-details__state"
              data-testid="issue-coding-doc-details-loading"
            >
              <ArrLoadingSpinner />
            </Box>
          ) : shouldShowError ? (
            <Box
              className="issue-coding-doc-details__error"
              data-testid="issue-coding-doc-details-error"
              role="alert"
              aria-live="assertive"
            >
              <Typography
                component="h3"
                className="typo-text subhead1_size text-headings"
                data-testid="issue-coding-doc-details-error-title"
              >
                {t('ai.review.jobResult.relevancyDocsApiFailureTitle')}
              </Typography>
              <ArrButton
                variant="primary"
                size="small"
                dataTestId="issue-coding-doc-details-error-retry-button"
                onClick={handleRetry}
              >
                {t('ai.review.jobResult.relevancyDocsApiFailureRetry')}
              </ArrButton>
            </Box>
          ) : (
            <Box
              className="issue-coding-content"
              data-testid="issue-coding-doc-details-content"
            >
              <Typography
                tabIndex={0}
                className="typo-text subhead4_size document-summary-title"
                data-testid="issue-coding-doc-details-title"
              >
                {t('ai.review.jobResult.summaryDocs.documentSummary')}
              </Typography>

              <Box
                className="issue-coding-metrics"
                data-testid="issue-coding-doc-details-metrics"
              >
                <Box
                  className="issue-coding-metric-box"
                  data-testid="issue-coding-doc-details-responsive-metric"
                >
                  <Typography
                    className="typo-text label3_size issue-coding-metric-label"
                    data-testid="issue-coding-doc-details-responsive-label"
                  >
                    {t('ai.review.jobResult.issueCodingDocDetails.responsive')}
                  </Typography>

                  <Typography
                    className="typo-text body3_size issue-coding-metric-value"
                    data-testid="issue-coding-doc-details-responsive-value"
                  >
                    {getDisplayValue(docSummary?.responsiveness)}
                  </Typography>
                </Box>

                <Box
                  className="issue-coding-metric-box"
                  data-testid="issue-coding-doc-details-confidence-metric"
                >
                  <Typography
                    className="typo-text label3_size issue-coding-metric-label"
                    data-testid="issue-coding-doc-details-confidence-label"
                  >
                    {t('ai.review.jobResult.issueCodingDocDetails.confidence')}
                  </Typography>

                  <Typography
                    className="typo-text body3_size issue-coding-metric-value"
                    data-testid="issue-coding-doc-details-confidence-value"
                  >
                    {formatConfidence(docSummary?.confidence)}
                  </Typography>
                </Box>
              </Box>

              <Box
                className="issue-coding-reason-section"
                data-testid="issue-coding-doc-details-reason-section"
              >
                <Typography
                  className="typo-text label3_size issue-coding-section-title"
                  data-testid="issue-coding-doc-details-reason-title"
                >
                  {t('ai.review.jobResult.issueCodingDocDetails.reason')}
                </Typography>

                <Typography
                  className="typo-text body3_size issue-coding-section-content"
                  data-testid="issue-coding-doc-details-reason-description"
                >
                  {getDisplayValue(docSummary?.reason)}
                </Typography>
              </Box>

              <Box
                className="issue-coding-tags-section"
                data-testid="issue-coding-doc-details-tags-section"
              >
                <Typography
                  className="typo-text label3_size issue-coding-section-title"
                  data-testid="issue-coding-doc-details-tags-title"
                >
                  {t('ai.review.jobResult.issueCodingDocDetails.tags')}
                </Typography>
                <Box className="display-chip-inline">
                  {tags.length > 0 ? (
                    tags.map((tag, index) => (
                      <Box
                        key={`${tag}-${index}`}
                        className="issue-coding-tag-chip"
                        data-testid={`issue-coding-doc-details-tag-chip-${index}`}
                      >
                        <Typography
                          className="typo-text label3_size issue-coding-tag-title"
                          data-testid={`issue-coding-doc-details-tag-text-${index}`}
                        >
                          {getDisplayValue(tag)}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography
                      className="typo-text body3_size issue-coding-section-content"
                      data-testid="issue-coding-doc-details-tags-empty"
                    >
                      {NO_DATA_PLACEHOLDER}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                className="issue-coding-banner-section"
                data-testid="issue-coding-doc-details-banner-section"
              >
                {isExcluded && (
                  <ArrBanner
                    variant="error"
                    type="contextual"
                    startIcon={<Error />}
                    className="issue-coding-excluded-banner"
                    dataTestId="issue-coding-doc-details-excluded-banner"
                    bannerContent={
                      <Box data-testid="issue-coding-doc-details-excluded-banner-content">
                        <Typography
                          className="typo-text subhead5_size issue-coding-banner-title"
                          data-testid="issue-coding-doc-details-excluded-banner-title"
                        >
                          {t('ai.review.jobResult.summaryDocs.excluded')}
                        </Typography>

                        <Typography
                          className="typo-text body4_size issue-coding-banner-description"
                          data-testid="issue-coding-doc-details-excluded-banner-description"
                        >
                          {getDisplayValue(docSummary?.excludedDetails)}
                        </Typography>
                      </Box>
                    }
                  />
                )}

                {isFlaggedForHumanReview && (
                  <ArrBanner
                    variant="warning"
                    type="contextual"
                    startIcon={<Warning sx={{alignSelf: 'flex-start'}} />}
                    className="issue-coding-review-banner"
                    dataTestId="issue-coding-doc-details-human-review-banner"
                    bannerContent={
                      <Box data-testid="issue-coding-doc-details-human-review-banner-content">
                        <Box
                          className="issue-coding-banner-header"
                          data-testid="issue-coding-doc-details-human-review-banner-header"
                        >
                          <Typography
                            className="typo-text subhead5_size issue-coding-banner-title"
                            data-testid="issue-coding-doc-details-human-review-banner-title"
                          >
                            {t(
                              'ai.review.jobResult.summaryDocs.flaggedForHumanReview'
                            )}
                          </Typography>

                          <Box
                            className="display-chip--warning-inline"
                            data-testid="issue-coding-doc-details-human-review-classification-chip"
                          >
                            {humanReviewTags.length > 0
                              ? humanReviewTags.map((humanReviewTag, index) => (
                                  <Box className="warning-label">
                                    <Typography
                                      className="typo-text label4_size"
                                      data-testid={`issue-coding-doc-details-human-review-classification-${index}`}
                                    >
                                      {getDisplayValue(humanReviewTag)}
                                    </Typography>
                                  </Box>
                                ))
                              : ''}
                          </Box>
                        </Box>

                        <Typography
                          className="typo-text body4_size issue-coding-banner-description"
                          data-testid="issue-coding-doc-details-human-review-banner-description"
                        >
                          {getDisplayValue(docSummary?.humanReviewDetails)}
                        </Typography>
                      </Box>
                    }
                  />
                )}
              </Box>

              <Box
                className="issue-coding-results-section"
                data-testid="issue-coding-doc-details-results-section"
              >
                <Typography
                  className="typo-text subhead4_size issue-coding-results-title"
                  data-testid="issue-coding-doc-details-results-title"
                >
                  {t(
                    'ai.review.jobResult.issueCodingDocDetails.issueCodingResults'
                  )}
                </Typography>
                <ArrDataGrid
                  rows={issueCodingRows}
                  columns={getIssueCodingResultColumns()}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                  showPerPageOptions
                  pageSizeOptions={[10]}
                  pagination
                  rowCount={issueCodingRows.length}
                  primaryKey="id"
                  hideFooter={true}
                  className="issue-coding-grid"
                  entityType={t('common.entries')}
                  autoHeight
                  getRowHeight={() => 'auto'}
                  showCellVerticalBorder
                  data-testid="issue-coding-doc-details-results-grid"
                />
              </Box>
            </Box>
          )}
        </Box>
      </ArrDrawer>
      {isErrorToastOpen && (
        <ArrToastNotification
          message={t('ai.review.jobResult.relevancyDocsApiFailureToast')}
          status={[ToastType.Error]}
          onClose={() => setIsErrorToastOpen(false)}
        />
      )}
    </>
  );
};

export default IssueCodingDocumentDetails;
